import { useCallback, useEffect, useState } from "react";
import { Status, SupportedLanguages } from "@repo/database";
import { ServiceSchema } from "@repo/validators";
import { useTranslation } from "react-i18next";

import type { Service } from "~/providers/service-provider";
import { useUser } from "~/hooks/use-user";
import { extractDirtyFields } from "~/lib/utils";
import { useServiceManager } from "~/managers/use-service-manager";
import { Button } from "../ui/button";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  useFieldArray,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";

export default function ServiceForm({ service }: { service?: Service }) {
  const { t } = useTranslation("common");
  const { user } = useUser();
  const { createService, updateService } = useServiceManager();
  const [selectedLanguage, setSelectedLanguage] =
    useState<SupportedLanguages | null>(null);
  const form = useForm({
    schema: ServiceSchema(),
    defaultValues: {
      name: service?.name ?? "",
      status: service?.status ?? Status.active,
      locales: service?.locales ?? [],
    },
  });

  const { fields, append } = useFieldArray({
    control: form.control,
    name: "locales",
  });

  const handleLanguageClick = useCallback(
    (language: SupportedLanguages) => {
      // Check if this language is already appended
      const alreadyExists = fields.some((f) => f.language === language);

      if (!alreadyExists) {
        append({
          language,
          name: "",
          description: "",
        });
      }
      setSelectedLanguage(language);
    },
    [append, fields],
  );

  const handleSubmit = form.handleSubmit(async (data) => {
    const dirtyFields = extractDirtyFields(data, form.formState.dirtyFields);
    if (service) {
      await updateService({
        id: service.id,
        ...dirtyFields,
      });
    } else {
      await createService(data);
    }
  });

  const selectedLocaleIndex = selectedLanguage
    ? fields.findIndex((f) => f.language === selectedLanguage)
    : -1;

  useEffect(() => {
    if (selectedLanguage) return;
    handleLanguageClick(user.language);
  }, [user, selectedLanguage, handleLanguageClick]);

  return (
    <Form {...form}>
      <form
        id="service-form"
        onSubmit={handleSubmit}
        className="flex flex-col gap-2"
      >
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("Services.name")}</FormLabel>
              <Input {...field} placeholder="Name" />
            </FormItem>
          )}
        />

        <div className="flex flex-col gap-2">
          <FormLabel>{t("Services.locales")}</FormLabel>
          <div className="flex flex-wrap gap-2">
            {Object.values(SupportedLanguages).map((language) => (
              <Button
                variant={selectedLanguage === language ? "brand" : "outline"}
                key={language}
                type="button"
                onClick={() => handleLanguageClick(language)}
              >
                {t(`Languages.${language}`, language)}
              </Button>
            ))}
          </div>
          {selectedLocaleIndex > -1 && (
            <div
              key={fields[selectedLocaleIndex].id}
              className="flex flex-col gap-2 p-2"
            >
              <FormField
                control={form.control}
                name={`locales.${selectedLocaleIndex}.name`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Services.name")}</FormLabel>
                    <Input {...field} placeholder="Name" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name={`locales.${selectedLocaleIndex}.description`}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("Services.description")}</FormLabel>
                    <Textarea {...field} placeholder="Description" />
                  </FormItem>
                )}
              />
            </div>
          )}
        </div>
      </form>
    </Form>
  );
}
